import { LitElement } from 'lit';
import Inputmask from 'inputmask/dist/inputmask.es6.js';

class ToujouInputMask extends LitElement {
  static get is() {
    return 'toujou-input-mask';
  }

  static get properties() {
    // All properties from inputmask/lib/defaults.js
    return {
      placeholder: { type: String },
      optionalmarker: { type: Array },
      quantifiermarker: { type: Array },
      groupmarker: { type: Array },
      alternatormarker: { type: String },
      escapeChar: { type: String },
      // needs tobe null instead of undefined as the extend method does not consider props with the undefined value
      mask: { type: String },
      // regular expression as a mask
      regex: { type: String },
      // repetitions of the mask: * ~ forever, otherwise specify an integer
      repeat: { type: Number },
      // true: allocated buffer for the mask and repetitions - false: allocate only if needed
      greedy: { type: Boolean },
      // automatically unmask when retrieving the value with $.fn.val or value if the browser supports __lookupGetter__ or getOwnPropertyDescriptor
      autoUnmask: { type: Boolean },
      // remove the mask before submitting the form.
      removeMaskOnSubmit: { type: Boolean },
      clearMaskOnLostFocus: { type: Boolean },
      // insert the input or overwrite the input
      insertMode: { type: Boolean },
      // show selected caret when insertmode = false
      insertModeVisual: { type: Boolean },
      // clear the incomplete input on blur
      clearIncomplete: { type: Boolean },
      alias: { type: String },
      // show the mask-placeholder when the input has focus
      showMaskOnFocus: { type: Boolean },
      // show the mask-placeholder when hovering the empty input
      showMaskOnHover: { type: Boolean },
      // a character which can be used to skip an optional part of a mask
      skipOptionalPartCharacter: ' ',
      // numericInput input direction style (input shifts to the left while holding the caret position)
      numericInput: { type: Boolean },
      // align to the right
      rightAlign: { type: Boolean },
      // pressing escape reverts the value to the value before focus
      undoOnEscape: { type: Boolean },
      // numeric basic properties
      // | ","
      radixPoint: '', // ".",
      // | "."
      groupSeparator: '', // ",",
      // try to keep the mask static while typing. Decisions to alter the mask will be posponed if possible
      keepStatic: { type: Boolean },
      // when enabled the caret position is set after the latest valid position on TAB
      positionCaretOnTab: { type: Boolean },
      // allows for tabbing through the different parts of the masked field
      tabThrough: { type: Boolean },
      // list with the supported input types
      supportsInputType: { type: Array },
      // specify keyCodes which should not be considered in the keypress event, otherwise the preventDefault will stop their default behavior especially in FF
      ignorables: { type: Array },
      // specify a definitionSymbol for static content, used to make matches for alternators
      staticDefinitionSymbol: { type: String },
      // just in time masking ~ only mask while typing, can n (number), true or false
      jitMasking: { type: Boolean },
      // return nothing instead of the buffertemplate when the user hasn't entered anything.
      nullable: { type: Boolean },
      // dev option - testing inputfallback behavior
      inputEventOnly: { type: Boolean },
      // disable value property patching
      noValuePatching: { type: Boolean },
      // none, lvp (based on the last valid position (default), radixFocus (position caret to radixpoint on initial click), select (select the whole input), ignore (ignore the click and continue the mask)
      positionCaretOnClick: { type: String },
      // mask-level casing. Options: null, "upper", "lower" or "title" or callback args => elem, test, pos, validPositions return charValue
      casing: { type: String },
      // specify the inputmode
      inputmode: { type: String },
      // import data-inputmask attributes
      importDataAttributes: { type: Boolean },
      // shift position of the mask entries on entry and deletion.
      shiftPositions: { type: Boolean },
      // use the default defined definitions from the prototype
      usePrototypeDefinitions: { type: Boolean },
      // Time to show validation error on form submit
      validationEventTimeOut: { type: Number },
      substitutes: { type: Object }, // define character substitutes
    };
  }

  constructor() {
    super();
    this.inputElementInputMaskMap = new WeakMap();
    this.parentForm = null;
    this._shouldValidate = false;

    this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);
  }

  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    this.parentForm = this.closest('form');

    if (!this.parentForm) {
      return;
    }

    if (this.parentForm.pristine !== undefined) {
      this.delayValidatorsUntilComplete();
    } else {
      const pristineReadyListener = () => {
        this.delayValidatorsUntilComplete();
        this.parentForm.removeEventListener('pristine-ready', pristineReadyListener);
      };
      this.parentForm.addEventListener('pristine-ready', pristineReadyListener);
    }
  }

  /**
   * @param {Map} _changedProperties
   */
  updated(_changedProperties) {
    if ([..._changedProperties.keys()].length === 0) {
      return;
    }

    this.inputElements.forEach((inputElement) => {
      if (this.inputElementInputMaskMap.has(inputElement)) {
        this.inputElementInputMaskMap.get(inputElement).option(this.inputMaskOptions);
      } else {
        const inputmask = new Inputmask(this.inputMaskOptions);
        this.inputElementInputMaskMap.set(inputElement, inputmask);
        this.inputElementInputMaskMap.get(inputElement).mask(inputElement);
        this.isValid = inputmask.isValid(inputElement.value);
      }
    });
  }

  delayValidatorsUntilComplete() {
    this.inputElements.forEach((inputElement) => {
      if (inputElement.pristine === undefined) {
        return;
      }

      const { pristine } = inputElement;
      pristine.originalValidators = pristine.validators;
      pristine.requiredValidators = pristine.validators.filter((validator) => validator.priority > 90);
      this.switchValidators(inputElement, true);
    });
  }

  /**
   * @param {HTMLInputElement} inputElement
   * @param {Boolean} initial
   */
  switchValidators(inputElement, initial = false) {
    if (inputElement.pristine === undefined) {
      return;
    }

    const { pristine } = inputElement;
    pristine.validators = this.shouldValidate ? pristine.originalValidators : pristine.requiredValidators;
    !initial && this.parentForm.pristine.validate(inputElement);
  }

  /**
   * @param {ToujouInputMask} toujouInputMaskElement
   * @returns {(function(*): void)|*}
   */
  onInputmaskEvent(toujouInputMaskElement) {
    const self = toujouInputMaskElement;
    return (event) => {
      const inputmask = toujouInputMaskElement.inputElementInputMaskMap.get(event.target);
      if (inputmask === undefined) {
        return;
      }
      self.shouldValidate = event.type === 'incomplete' || inputmask.isValid(event.target.value);
    };
  }

  /**
   * Upon hitting enter we need to switch on full validation before submit
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key === 'Enter') {
      this.shouldValidate = true;
    }
  }

  set shouldValidate(shouldValidate) {
    if (shouldValidate !== this._shouldValidate) {
      this._shouldValidate = shouldValidate;
      this.inputElements.forEach((inputElement) => {
        this.switchValidators(inputElement);
      });
    }
  }

  get shouldValidate() {
    return this._shouldValidate;
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  get inputElements() {
    const inputElements = this.querySelectorAll('input:not([type="hidden"])');
    return [...inputElements];
  }

  /**
   * @returns {Object}
   */
  get inputMaskOptions() {
    const { properties } = ToujouInputMask;
    const options = {
      oncomplete: this.onInputmaskEvent(this),
      onincomplete: this.onInputmaskEvent(this),
      oncleared: this.onInputmaskEvent(this),
    };
    Object.keys(properties).forEach((key) => {
      const value = this[key];
      if (value !== undefined) {
        options[key] = value;
      }
    });
    return options;
  }
}

customElements.define(ToujouInputMask.is, ToujouInputMask);
